import React from "react";
import { X, FileText, MessageSquare, StickyNote, Sparkles, Lightbulb, Send, Layers, ClipboardCheck, CalendarClock } from "lucide-react";

/**
 * Reusable "What can I say?" help sheet for the Guru Voice agent.
 * Controlled modal — props: { open, onClose }. Rendered at z-[60] so it can
 * sit above the voice agent sheet (z-50) when opened from inside it.
 */

const GROUPS = [
  {
    key: "estimate",
    label: "Build estimates",
    icon: FileText,
    color: "#2563EB",
    blurb: "Items are matched to your Pricebook and priced automatically.",
    examples: [
      "Add two hours of labor",
      "Add a panel inspection and a programmable thermostat",
      "Add three filters for Jennifer Williams",
      "Add two hours of after-hours labor",
      "Add a custom bracket for fifty dollars",
    ],
  },
  {
    key: "message",
    label: "Message a teammate",
    icon: MessageSquare,
    color: "#7C3AED",
    blurb: "Sends a 1:1 direct message to the person you name.",
    examples: [
      "Send Dana a message that I'm running late",
      "Message Marcus: bring the spare capacitor",
      "Tell Sarah the customer approved the estimate",
    ],
  },
  {
    key: "note",
    label: "Add a customer note",
    icon: StickyNote,
    color: "#0891B2",
    blurb: "Adds a timestamped note to the customer's file.",
    examples: [
      "Add a note to Jennifer Williams' file: gate code is 4321",
      "Note on this customer: dog in the backyard, call first",
      "Make a note that the unit is 12 years old",
    ],
  },
  {
    key: "send",
    label: "Send to the customer",
    icon: Send,
    color: "#0EA5E9",
    blurb: "Emails or texts an estimate/invoice with a secure link (email also attaches a PDF).",
    examples: [
      "Email this estimate to the customer",
      "Text John Smith his estimate",
      "Email Jennifer her latest invoice",
    ],
  },
  {
    key: "jobpay",
    label: "Update jobs & collect payment",
    icon: ClipboardCheck,
    color: "#EA580C",
    blurb: "Change a job's status or record a payment you collected in the field.",
    examples: [
      "Mark this job complete",
      "Start this job",
      "Collect a $250 cash payment",
      "Record a check payment on Jennifer's invoice",
    ],
  },
  {
    key: "appointments",
    label: "Check & book appointments",
    icon: CalendarClock,
    color: "#16A34A",
    blurb: "Reads and writes the company calendar — dates like \u201Ctomorrow\u201D use today's real date.",
    examples: [
      "What appointments do I have on Friday?",
      "What times are available tomorrow?",
      "Book an appointment for Jennifer Williams on Friday at 2pm",
      "Cancel the appointment with John Smith on Thursday",
      "Reschedule Jennifer's appointment from Friday to Monday at 10am",
    ],
  },
  {
    key: "multi",
    label: "Do several at once",
    icon: Layers,
    color: "#DB2777",
    blurb: "Chain actions in one breath — Guru runs them in order and confirms once.",
    examples: [
      "Make an estimate for this customer with 2 hours of labor and email it to them",
      "Mark this job complete and text the customer their invoice",
      "Add a diagnostic fee and text the estimate to the customer",
    ],
  },
];

const TIPS = [
  "Chain actions in one command — say \u201Cmake an estimate and email it\u201D — and Guru runs them in order.",
  "If a word matches more than one Pricebook item (e.g. just \u201Clabor\u201D), Guru shows a quick pick-a-variant card.",
  "Name the variant to skip the prompt \u2014 say \u201Cafter-hours labor\u201D.",
  "The Pricebook price always wins over a price you say out loud for catalog items.",
  "When you're inside a job, say \u201Cthis customer\u201D, \u201Cthis job\u201D, or \u201Chere\u201D.",
  "Guru always reads back what it heard and waits for you to confirm before saving.",
];

export default function VoiceCommandsSheet({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center" data-testid="voice-commands-sheet">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom duration-200 max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-[14px] font-bold text-slate-900">What can I say?</div>
            <div className="text-[11px] text-slate-400">Guru Assistant voice commands</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="voice-commands-close-btn" aria-label="Close" className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="p-5 space-y-5 overflow-y-auto">
          {GROUPS.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.key} data-testid={`voice-commands-group-${g.key}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: `${g.color}15` }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: g.color }} />
                  </div>
                  <span className="text-[13px] font-bold text-slate-800">{g.label}</span>
                </div>
                <div className="text-[11.5px] text-slate-400 mb-2 ml-8">{g.blurb}</div>
                <ul className="space-y-1.5 ml-8">
                  {g.examples.map((ex, i) => (
                    <li key={i} className="text-[13px] text-slate-700 leading-snug flex items-start gap-1.5">
                      <span className="text-slate-300 mt-0.5">&ldquo;</span>
                      <span>{ex}</span>
                      <span className="text-slate-300">&rdquo;</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Tips */}
          <div className="rounded-lg border border-slate-200 px-3.5 py-3" data-testid="voice-commands-tips" style={{backgroundColor: '#e3e4e6'}}>
            <div className="flex items-center gap-1.5 mb-2">
              <Lightbulb className="h-3.5 w-3.5" style={{color: '#000000'}} />
              <span className="text-[12px] font-bold uppercase tracking-wide" style={{color: '#000000'}}>Good to know</span>
            </div>
            <ul className="space-y-1.5">
              {TIPS.map((t, i) => (
                <li key={i} className="text-[12px] leading-snug flex gap-1.5" style={{color: '#000000'}}>
                  <span style={{color: '#000000'}}>&bull;</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
